import { API_URL } from '../config/env';
import { logger } from '../utils/logger';

let cachedToken: string | null = null;
let tokenExpiry: number | null = null;
let inFlightRequest: Promise<string> | null = null;

// CSRF tokens typically expire after 1 hour, cache for 50 minutes to be safe
const TOKEN_CACHE_DURATION = 50 * 60 * 1000; // 50 minutes

const requestCsrfToken = async (): Promise<string> => {
  const response = await fetch(`${API_URL}/v1/csrf-token`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch CSRF token: ${response.status}`);
  }

  const data = await response.json();
  if (!data?.csrfToken) {
    throw new Error('CSRF token missing from response');
  }

  cachedToken = data.csrfToken;
  tokenExpiry = Date.now() + TOKEN_CACHE_DURATION;
  return cachedToken as string;
};

export const getCsrfToken = async (): Promise<string> => {
  // Check if we have a valid, non-expired token
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  // Token is expired or missing, clear it
  if (cachedToken && tokenExpiry && Date.now() >= tokenExpiry) {
    logger.debug('[CSRF] Token expired, clearing cache');
    clearCsrfToken();
  }

  if (!inFlightRequest) {
    inFlightRequest = requestCsrfToken().finally(() => {
      inFlightRequest = null;
    });
  }

  return inFlightRequest;
};

export const clearCsrfToken = () => {
  cachedToken = null;
  tokenExpiry = null;
};

export const refreshCsrfToken = async (): Promise<string> => {
  clearCsrfToken();
  try {
    return await getCsrfToken();
  } catch (error) {
    logger.error('[CSRF] Failed to refresh token', error);
    throw error;
  }
};

export const attachCsrfToken = async (headers: Record<string, string> = {}) => {
  const token = await getCsrfToken();
  return {
    ...headers,
    'X-CSRF-Token': token
  };
};
